<template>
  <teleport to="body">
    <transition name="modal-fade">
      <div v-if="showModal" class="modal-overlay" v-on:click.self="closeModal">
        <transition name="slide-in">
          <div v-if="showModal" class="modal-panel">
            <header class="modal-head">
              <div>
                <div class="modal-eyebrow" :class="statusTone">
                  <span class="status-dot"></span>
                  {{ statusLabel }}
                </div>
                <h2>{{ formattedAmount }}</h2>
                <p>{{ bankLabel }} · {{ verification.transactionReference || verification.transactionId || "—" }}</p>
              </div>
              <button type="button" class="icon-button" v-on:click="closeModal" aria-label="Close">
                <font-awesome-icon icon="fa-solid fa-xmark" />
              </button>
            </header>

            <div class="modal-body">
              <section class="form-section">
                <h3>Transaction</h3>
                <dl class="detail-grid">
                  <div class="detail">
                    <dt>Status</dt>
                    <dd><span class="badge" :class="statusBadgeClass">{{ statusLabel }}</span></dd>
                  </div>
                  <div class="detail">
                    <dt>Bank</dt>
                    <dd>{{ bankLabel }}</dd>
                  </div>
                  <div class="detail">
                    <dt>Amount</dt>
                    <dd>{{ formattedAmount }}</dd>
                  </div>
                  <div class="detail">
                    <dt>Date</dt>
                    <dd>{{ $filters.getDateAndTime(verification.date) }}</dd>
                  </div>
                  <div class="detail full">
                    <dt>Transaction Reference</dt>
                    <dd class="mono">{{ verification.transactionReference || "—" }}</dd>
                  </div>
                  <div class="detail full">
                    <dt>Transaction ID</dt>
                    <dd class="mono">{{ verification.transactionId || "—" }}</dd>
                  </div>
                </dl>
              </section>

              <section class="form-section">
                <h3>Payer</h3>
                <dl class="detail-grid">
                  <div class="detail">
                    <dt>Account Name</dt>
                    <dd>{{ verification.accountName || "—" }}</dd>
                  </div>
                  <div class="detail">
                    <dt>Account Number</dt>
                    <dd class="mono">{{ verification.accountNumber || "—" }}</dd>
                  </div>
                </dl>
              </section>

              <section v-if="hasReceiver" class="form-section">
                <h3>Receiver</h3>
                <dl class="detail-grid">
                  <div class="detail">
                    <dt>Account Name</dt>
                    <dd>{{ receiverName || "—" }}</dd>
                  </div>
                  <div class="detail">
                    <dt>Account Number</dt>
                    <dd class="mono">{{ receiverAccountNumber || "—" }}</dd>
                  </div>
                </dl>
              </section>

              <section v-if="imageUrl" class="form-section">
                <h3>Receipt</h3>
                <a :href="imageUrl" target="_blank" rel="noopener" class="receipt-link">
                  <img :src="imageUrl" alt="Payment receipt" class="receipt-image" />
                </a>
              </section>

              <section class="form-section">
                <h3>Audit</h3>
                <dl class="detail-grid">
                  <div class="detail">
                    <dt>Verified By</dt>
                    <dd>{{ personName(verification.verifiedByUser) }}</dd>
                  </div>
                  <div class="detail">
                    <dt>Verified At</dt>
                    <dd>{{ verification.verifiedAt ? $filters.getDateAndTime(verification.verifiedAt) : "—" }}</dd>
                  </div>
                </dl>
              </section>
            </div>

            <footer class="modal-foot">
              <button type="button" class="ghost-button" v-on:click="closeModal">Close</button>
              <button
                v-if="verification.status !== 'verified'"
                type="button"
                class="primary-button"
                :disabled="isLoading"
                v-on:click="approve"
              >
                <span v-if="!isLoading">Approve</span>
                <span v-else>Saving...</span>
              </button>
            </footer>
          </div>
        </transition>
      </div>
    </transition>
  </teleport>
</template>

<script>
import { UPDATE_VERIFICATION_STATUS } from "@/store/action_types";
import { STORAGE_URL, TOAST_GLOBAL_CONFIG, VERIFICATION_IMAGE_DIR } from "@/utils/const";
import { BANK_LABELS, VERIFICATION_STATUS } from "@/utils/enums";
import { library } from "@fortawesome/fontawesome-svg-core";
import { faXmark } from "@fortawesome/free-solid-svg-icons";

library.add(faXmark);

const STATUS_BADGE = {
  [VERIFICATION_STATUS.verified]: "badge-success",
  [VERIFICATION_STATUS.pending]: "badge-warning",
  [VERIFICATION_STATUS.rejected]: "badge-danger",
  [VERIFICATION_STATUS.failed]: "badge-danger",
};

export default {
  props: { verification: { type: Object, required: true } },
  emits: ["close-detail-modal", "refresh-verifications"],
  data() {
    return {
      showModal: true,
    };
  },
  computed: {
    isLoading() {
      return this.$store.getters.verification.isLoading;
    },
    bankLabel() {
      return BANK_LABELS[this.verification.bank] || String(this.verification.bank || "").toUpperCase();
    },
    statusLabel() {
      const s = this.verification.status || "";
      return s ? s.charAt(0).toUpperCase() + s.slice(1) : "—";
    },
    statusBadgeClass() {
      return STATUS_BADGE[this.verification.status] || "badge-secondary";
    },
    statusTone() {
      return `tone-${this.verification.status || "pending"}`;
    },
    formattedAmount() {
      const amount = Number(this.verification.amount);
      if (isNaN(amount)) return "—";
      return this.$filters.formatPrice(amount, this.verification.currency || null);
    },
    imageUrl() {
      if (!this.verification.image) return null;
      return `${STORAGE_URL}/${VERIFICATION_IMAGE_DIR}/${this.verification.image}`;
    },
    // Receiver = who the payment was made to, read from the bank receipt; falls back to the
    // merchant's linked recipient account for verifications done before that was captured.
    receiverName() {
      const v = this.verification;
      return v.receiverName || v.recipientAccount?.accountName || v.recipientAccount?.name || "";
    },
    receiverAccountNumber() {
      const v = this.verification;
      return v.receiverAccountNumber || v.recipientAccount?.accountNumber || "";
    },
    hasReceiver() {
      return !!(this.receiverName || this.receiverAccountNumber);
    },
  },
  methods: {
    personName(person) {
      if (!person) return "—";
      return this.$filters.fullName(person.fname, person.lname) || person.email || "—";
    },
    closeModal() {
      this.$emit("close-detail-modal");
    },
    approve() {
      this.$store
        .dispatch(UPDATE_VERIFICATION_STATUS, {
          id: this.verification.id,
          status: VERIFICATION_STATUS.verified,
        })
        .then((res) => {
          if (res) {
            this.$toast.success("Verification approved.", TOAST_GLOBAL_CONFIG);
            this.$emit("refresh-verifications");
            this.closeModal();
          }
        });
    },
  },
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  justify-content: flex-end;
  background: rgba(15, 23, 42, 0.45);
  backdrop-filter: blur(2px);
}

.modal-panel {
  width: 520px;
  max-width: 95vw;
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border-left: 1px solid var(--ehs-color-border);
  box-shadow: -24px 0 60px rgba(15, 23, 42, 0.18);
  color: var(--ehs-color-ink);
}

.modal-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  padding: 22px 24px 18px;
  border-bottom: 1px solid var(--ehs-color-border);
}

.modal-eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 9px;
  padding: 5px 11px;
  margin-bottom: 8px;
  border: 1px solid var(--ehs-color-border);
  border-radius: var(--ehs-radius-pill);
  background: var(--ehs-color-primary-soft);
  color: var(--ehs-color-primary-active);
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--ehs-color-soft);
}

.tone-verified { background: var(--ehs-color-success-soft); color: #047857; border-color: transparent; }
.tone-verified .status-dot { background: var(--ehs-color-success); }
.tone-pending { background: var(--ehs-color-warning-soft); color: #92400e; border-color: transparent; }
.tone-pending .status-dot { background: var(--ehs-color-warning); }
.tone-rejected { background: var(--ehs-color-danger-soft); color: #b91c1c; border-color: transparent; }
.tone-rejected .status-dot { background: var(--ehs-color-danger); }
.tone-failed { background: var(--ehs-color-canvas-strong); color: var(--ehs-color-text); border-color: transparent; }

.modal-head h2 {
  margin: 0;
  font-family: var(--ehs-font-display);
  font-size: 1.5rem;
  font-weight: 700;
  letter-spacing: -0.025em;
}

.modal-head p {
  margin: 4px 0 0;
  color: var(--ehs-color-muted);
  font-size: 0.84rem;
}

.icon-button {
  width: 38px;
  height: 38px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--ehs-color-border);
  background: #ffffff;
  border-radius: 50%;
  color: var(--ehs-color-ink);
  cursor: pointer;
  box-shadow: none !important;
  transition: background 0.18s ease, color 0.18s ease, border-color 0.18s ease;
}

.icon-button:hover {
  background: var(--ehs-color-primary-soft);
  color: var(--ehs-color-primary-hover);
}

.modal-body {
  flex: 1;
  overflow-y: auto;
  padding: 22px 24px 32px;
  background: var(--ehs-color-canvas);
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.form-section {
  padding: 18px 20px;
  background: #ffffff;
  border: 1px solid var(--ehs-color-border);
  border-radius: var(--ehs-radius-lg);
}

.form-section h3 {
  margin: 0 0 14px;
  font-family: var(--ehs-font-display);
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--ehs-color-muted);
}

.detail-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px 16px;
  margin: 0;
}

.detail.full {
  grid-column: 1 / -1;
}

.detail dt {
  margin: 0 0 3px;
  font-size: 0.72rem;
  font-weight: 600;
  color: var(--ehs-color-soft);
}

.detail dd {
  margin: 0;
  font-size: 0.88rem;
  font-weight: 500;
  color: var(--ehs-color-ink);
  word-break: break-word;
}

.detail dd.mono {
  font-family: var(--ehs-font-mono);
  font-size: 0.82rem;
}

.receipt-link {
  display: block;
}

.receipt-image {
  width: 100%;
  max-height: 360px;
  object-fit: contain;
  border-radius: var(--ehs-radius-md);
  border: 1px solid var(--ehs-color-border);
  background: var(--ehs-color-canvas);
}

.modal-foot {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 24px;
  border-top: 1px solid var(--ehs-color-border);
  background: #ffffff;
}

.ghost-button,
.primary-button {
  border-radius: var(--ehs-radius-pill);
  border: 1px solid transparent;
  padding: 10px 20px;
  font-size: 0.84rem;
  font-weight: 600;
  cursor: pointer;
  box-shadow: none !important;
  transition: background 0.18s ease, border-color 0.18s ease, color 0.18s ease;
}

.ghost-button {
  background: #ffffff;
  color: var(--ehs-color-ink);
  border-color: var(--ehs-color-border);
}

.ghost-button:hover {
  background: var(--ehs-color-canvas);
  border-color: var(--ehs-color-border-strong);
}

.primary-button {
  background: var(--ehs-color-success);
  color: #ffffff;
  border-color: var(--ehs-color-success);
}

.primary-button:hover {
  background: #059669;
  border-color: #059669;
}

.primary-button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.25s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.slide-in-enter-active,
.slide-in-leave-active {
  transition: transform 0.32s cubic-bezier(0.22, 1, 0.36, 1);
}

.slide-in-enter-from,
.slide-in-leave-to {
  transform: translateX(100%);
}

@media (max-width: 991.98px) {
  .modal-panel {
    width: 100%;
    max-width: 100%;
  }

  .detail-grid {
    grid-template-columns: 1fr;
  }

  .modal-foot {
    flex-wrap: wrap;
  }
}
</style>
